import { useEffect, useState } from "react";
import { toast } from "sonner";
import { LogOut, Eye, EyeOff, Download } from "lucide-react";
import PageHeader from "@/components/site/PageHeader";
import {
    listReps,
    listContacts,
    verifyAdmin,
    setAdminToken,
    getAdminToken,
    updateRepStatus,
    updateContactStatus,
    downloadRepsCsv,
    downloadContactsCsv,
} from "@/lib/api";
import {
    Tabs,
    TabsList,
    TabsTrigger,
    TabsContent,
} from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const STATUS_OPTIONS = [
    { value: "new", label: "New" },
    { value: "contacted", label: "Contacted" },
    { value: "matched", label: "Matched" },
];

const STATUS_STYLES = {
    new: "bg-slate-100 text-slate-700 border-slate-200",
    contacted: "bg-amber-50 text-amber-800 border-amber-200",
    matched: "bg-emerald-50 text-emerald-800 border-emerald-200",
};

function triggerCsvDownload(blob, filename) {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
}

export default function AdminPage() {
    const [authed, setAuthed] = useState(false);
    const [checking, setChecking] = useState(true);

    // On mount, validate existing token (if any)
    useEffect(() => {
        const token = getAdminToken();
        if (!token) {
            setChecking(false);
            return;
        }
        verifyAdmin(token)
            .then(() => setAuthed(true))
            .catch(() => {
                setAdminToken("");
                setAuthed(false);
            })
            .finally(() => setChecking(false));
    }, []);

    const handleLogout = () => {
        setAdminToken("");
        setAuthed(false);
    };

    if (checking) {
        return (
            <div data-testid="admin-page">
                <PageHeader
                    eyebrow="Admin"
                    title="Submissions."
                    description="Internal view of leads submitted through 1099er."
                    testId="admin-header"
                />
                <section className="bg-[#FAFAFA] border-b border-slate-200">
                    <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 text-center text-sm text-slate-500">
                        Checking session...
                    </div>
                </section>
            </div>
        );
    }

    if (!authed) {
        return <AdminLogin onSuccess={() => setAuthed(true)} />;
    }

    return <AdminDashboard onLogout={handleLogout} />;
}

function AdminLogin({ onSuccess }) {
    const [token, setToken] = useState("");
    const [loading, setLoading] = useState(false);
    const [showToken, setShowToken] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");

    const handleSubmit = async (e) => {
        e.preventDefault();
        const clean = token.trim();
        if (!clean) {
            setErrorMsg("Enter the admin token.");
            toast.error("Enter the admin token.");
            return;
        }
        setLoading(true);
        setErrorMsg("");
        try {
            await verifyAdmin(clean);
            setAdminToken(clean);
            onSuccess();
        } catch (err) {
            const msg =
                "Invalid admin token. Tokens are case-sensitive and include dashes.";
            setErrorMsg(msg);
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div data-testid="admin-page">
            <PageHeader
                eyebrow="Admin"
                title="Restricted."
                description="Enter the admin access token to view submissions."
                testId="admin-header"
            />
            <section className="bg-[#FAFAFA] border-b border-slate-200">
                <div className="max-w-md mx-auto px-6 md:px-12 py-16">
                    <form
                        data-testid="admin-login-form"
                        onSubmit={handleSubmit}
                        className="bg-white border border-slate-200 rounded-2xl p-8 space-y-5"
                    >
                        <div className="space-y-2">
                            <Label className="text-sm font-medium text-slate-900">
                                Admin token
                            </Label>
                            <div className="relative">
                                <Input
                                    data-testid="admin-token-input"
                                    type={showToken ? "text" : "password"}
                                    value={token}
                                    onChange={(e) => {
                                        setToken(e.target.value);
                                        if (errorMsg) setErrorMsg("");
                                    }}
                                    placeholder="Enter access token"
                                    className="bg-white border-slate-200 h-11 pr-11 font-mono tracking-tight"
                                    autoFocus
                                    autoComplete="off"
                                    autoCorrect="off"
                                    autoCapitalize="off"
                                    spellCheck={false}
                                    name="admin-token"
                                />
                                <button
                                    type="button"
                                    data-testid="admin-token-toggle-visibility"
                                    onClick={() => setShowToken((v) => !v)}
                                    className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1.5 text-slate-500 hover:text-slate-900 transition-colors"
                                    aria-label={showToken ? "Hide token" : "Show token"}
                                >
                                    {showToken ? <EyeOff size={16} /> : <Eye size={16} />}
                                </button>
                            </div>
                            <p className="text-[11px] text-slate-500 leading-relaxed">
                                Case-sensitive. Include the dashes.
                            </p>
                            {errorMsg && (
                                <p
                                    data-testid="admin-login-error"
                                    className="text-xs text-red-600 leading-relaxed"
                                >
                                    {errorMsg}
                                </p>
                            )}
                        </div>
                        <button
                            type="submit"
                            data-testid="admin-login-btn"
                            disabled={loading}
                            className="w-full bg-slate-900 text-white text-sm font-medium px-6 py-3 rounded-md hover:bg-slate-800 transition-colors disabled:opacity-60"
                        >
                            {loading ? "Verifying..." : "Sign in"}
                        </button>
                    </form>
                </div>
            </section>
        </div>
    );
}

function AdminDashboard({ onLogout }) {
    const [reps, setReps] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState("all");
    const [search, setSearch] = useState("");

    useEffect(() => {
        Promise.all([listReps(), listContacts()])
            .then(([r, m]) => {
                setReps(r.data || []);
                setContacts(m.data || []);
            })
            .catch((e) => {
                console.error(e);
                if (e?.response?.status === 401) {
                    setAdminToken("");
                    window.location.reload();
                }
            })
            .finally(() => setLoading(false));
    }, []);

    const handleRepStatus = async (id, status) => {
        const prev = reps;
        setReps((rs) => rs.map((r) => (r.id === id ? { ...r, status } : r)));
        try {
            await updateRepStatus(id, status);
            toast.success(`Status updated to ${status}.`);
        } catch (err) {
            console.error(err);
            setReps(prev);
            toast.error("Could not update status.");
        }
    };

    const handleContactStatus = async (id, status) => {
        const prev = contacts;
        setContacts((cs) => cs.map((c) => (c.id === id ? { ...c, status } : c)));
        try {
            await updateContactStatus(id, status);
            toast.success(`Status updated to ${status}.`);
        } catch (err) {
            console.error(err);
            setContacts(prev);
            toast.error("Could not update status.");
        }
    };

    const handleExport = async (kind) => {
        try {
            const res = kind === "reps" ? await downloadRepsCsv() : await downloadContactsCsv();
            const stamp = new Date().toISOString().slice(0, 10);
            triggerCsvDownload(res.data, `1099er-${kind}-${stamp}.csv`);
        } catch (err) {
            console.error(err);
            toast.error("CSV export failed.");
        }
    };

    const matchesFilters = (item, fields) => {
        if (statusFilter !== "all" && (item.status || "new") !== statusFilter) return false;
        if (!search.trim()) return true;
        const q = search.trim().toLowerCase();
        return fields.some((f) => (item[f] || "").toString().toLowerCase().includes(q));
    };

    const visibleReps = reps.filter((r) =>
        matchesFilters(r, ["full_name", "email", "phone", "selected_opportunity"])
    );
    const visibleContacts = contacts.filter((c) =>
        matchesFilters(c, ["name", "email", "role", "message"])
    );

    return (
        <div data-testid="admin-page">
            <PageHeader
                eyebrow="Admin"
                title="Submissions."
                description="Internal view of rep profiles and contact messages."
                testId="admin-header"
            />
            <section className="bg-[#FAFAFA] border-b border-slate-200">
                <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16">
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                        <div className="flex flex-wrap items-center gap-3">
                            <input
                                data-testid="admin-search-input"
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search name, email, message..."
                                className="h-9 text-sm bg-white border border-slate-200 rounded-md px-3 min-w-[240px] focus:outline-none focus:border-slate-900"
                            />
                            <select
                                data-testid="admin-status-filter"
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="h-9 text-sm bg-white border border-slate-200 rounded-md px-3 focus:outline-none focus:border-slate-900"
                            >
                                <option value="all">All statuses</option>
                                {STATUS_OPTIONS.map((o) => (
                                    <option key={o.value} value={o.value}>
                                        {o.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <button
                            data-testid="admin-logout-btn"
                            onClick={onLogout}
                            className="inline-flex items-center gap-2 text-xs text-slate-600 hover:text-slate-900 transition-colors"
                        >
                            <LogOut size={14} /> Sign out
                        </button>
                    </div>
                    <Tabs defaultValue="reps" className="w-full">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <TabsList data-testid="admin-tabs" className="bg-white border border-slate-200">
                                <TabsTrigger value="reps" data-testid="admin-tab-reps">
                                    Reps ({visibleReps.length})
                                </TabsTrigger>
                                <TabsTrigger value="contacts" data-testid="admin-tab-contacts">
                                    Contact ({visibleContacts.length})
                                </TabsTrigger>
                            </TabsList>
                            <div className="flex gap-2">
                                <button
                                    data-testid="admin-export-reps-btn"
                                    onClick={() => handleExport("reps")}
                                    className="inline-flex items-center gap-2 bg-white text-slate-900 border border-slate-200 hover:border-slate-900 text-xs font-medium px-3 py-2 rounded-md transition-colors"
                                >
                                    <Download size={14} /> Export reps CSV
                                </button>
                                <button
                                    data-testid="admin-export-contacts-btn"
                                    onClick={() => handleExport("contacts")}
                                    className="inline-flex items-center gap-2 bg-white text-slate-900 border border-slate-200 hover:border-slate-900 text-xs font-medium px-3 py-2 rounded-md transition-colors"
                                >
                                    <Download size={14} /> Export contacts CSV
                                </button>
                            </div>
                        </div>
                        <TabsContent value="reps" className="mt-6">
                            {loading ? (
                                <Loader />
                            ) : visibleReps.length === 0 ? (
                                <Empty label="No rep submissions match the current filters." />
                            ) : (
                                <div className="space-y-3" data-testid="admin-reps-list">
                                    {visibleReps.map((r) => (
                                        <RepCard
                                            key={r.id}
                                            rep={r}
                                            onStatusChange={handleRepStatus}
                                        />
                                    ))}
                                </div>
                            )}
                        </TabsContent>
                        <TabsContent value="contacts" className="mt-6">
                            {loading ? (
                                <Loader />
                            ) : visibleContacts.length === 0 ? (
                                <Empty label="No contact messages match the current filters." />
                            ) : (
                                <div className="space-y-3" data-testid="admin-contacts-list">
                                    {visibleContacts.map((m) => (
                                        <ContactCard
                                            key={m.id}
                                            contact={m}
                                            onStatusChange={handleContactStatus}
                                        />
                                    ))}
                                </div>
                            )}
                        </TabsContent>
                    </Tabs>
                </div>
            </section>
        </div>
    );
}

function StatusPill({ value, onChange, testId }) {
    const status = value || "new";
    return (
        <select
            data-testid={testId}
            value={status}
            onChange={(e) => onChange(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            className={`text-[11px] font-medium uppercase tracking-[0.12em] px-2.5 py-1 rounded-full border cursor-pointer focus:outline-none ${STATUS_STYLES[status] || STATUS_STYLES.new}`}
        >
            {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                    {o.label}
                </option>
            ))}
        </select>
    );
}

function Loader() {
    return (
        <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center text-sm text-slate-500">
            Loading...
        </div>
    );
}

function Empty({ label }) {
    return (
        <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center text-sm text-slate-500">
            {label}
        </div>
    );
}

function Row({ label, value }) {
    if (!value || (Array.isArray(value) && value.length === 0)) return null;
    return (
        <div className="flex flex-col sm:flex-row sm:justify-between gap-1 py-2 border-b border-slate-100 last:border-b-0">
            <span className="text-xs font-semibold tracking-[0.1em] uppercase text-slate-500">
                {label}
            </span>
            <span className="text-sm text-slate-800 sm:text-right max-w-2xl break-words">
                {Array.isArray(value) ? value.join(", ") : value}
            </span>
        </div>
    );
}

function RepCard({ rep, onStatusChange }) {
    return (
        <details
            data-testid={`admin-rep-${rep.id}`}
            className="bg-white border border-slate-200 rounded-xl overflow-hidden group"
        >
            <summary className="cursor-pointer list-none px-6 py-4 flex items-center justify-between hover:bg-slate-50">
                <div>
                    <p className="font-display text-base font-semibold text-slate-900">
                        {rep.full_name}
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5">
                        {rep.email} · {new Date(rep.created_at).toLocaleDateString()}
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <StatusPill
                        value={rep.status}
                        onChange={(status) => onStatusChange(rep.id, status)}
                        testId={`admin-rep-${rep.id}-status`}
                    />
                    <span className="text-xs text-slate-500 group-open:rotate-180 transition-transform">
                        ▾
                    </span>
                </div>
            </summary>
            <div className="px-6 pb-5">
                <Row label="Email" value={rep.email} />
                <Row label="Phone" value={rep.phone} />
                <Row label="LinkedIn" value={rep.linkedin_url} />
                <Row label="Primary Market" value={rep.primary_market} />
                <Row label="Specialties" value={rep.specialties} />
                <Row label="Selected Opportunity" value={rep.selected_opportunity} />
                <Row label="Products to Rep" value={rep.products_to_rep} />
                <Row label="Add-on / Emergent Access" value={rep.addon_emergent_access} />
                <Row label="Case Support Comfort" value={rep.case_support_comfort} />
                <Row label="Knows Local Distributor" value={rep.knows_local_distributor} />
                <Row label="Distributor Notes" value={rep.distributor_notes} />
                <Row label="Open to independent" value={rep.open_to_independent} />
                <Row label="Conflicts" value={rep.conflicts} />
                <Row label="Why interested" value={rep.why_interested} />
                <Row label="Notes" value={rep.notes} />
            </div>
        </details>
    );
}

function ContactCard({ contact, onStatusChange }) {
    return (
        <details
            data-testid={`admin-contact-${contact.id}`}
            className="bg-white border border-slate-200 rounded-xl overflow-hidden group"
        >
            <summary className="cursor-pointer list-none px-6 py-4 flex items-center justify-between hover:bg-slate-50">
                <div>
                    <p className="font-display text-base font-semibold text-slate-900">
                        {contact.name}
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5">
                        {contact.email}
                        {contact.role ? ` · ${contact.role}` : ""} ·{" "}
                        {new Date(contact.created_at).toLocaleDateString()}
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <StatusPill
                        value={contact.status}
                        onChange={(status) => onStatusChange(contact.id, status)}
                        testId={`admin-contact-${contact.id}-status`}
                    />
                    <span className="text-xs text-slate-500 group-open:rotate-180 transition-transform">
                        ▾
                    </span>
                </div>
            </summary>
            <div className="px-6 pb-5">
                <Row label="Email" value={contact.email} />
                <Row label="Role" value={contact.role} />
                <Row label="Message" value={contact.message} />
            </div>
        </details>
    );
}
